import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { trim } from 'lodash';
import classnames from 'classnames';

import UnitListItem from 'components/unit/UnitList/UnitListItem';
import { UnitDescription } from 'components/unit/UnitDescription';
import { UnitPrice } from 'components/unit/UnitPrice';
import { UnitIcon } from 'components/unit/UnitIcon';
import { UnitReservationForm } from 'components/unit/UnitReservationForm';
import { Expand } from 'components/core/Expand';
import { IconText } from 'components/core/IconText';
import { getScarcityFlag } from 'utils/store/getScarcityFlag';

import clockImage from 'assets/images/icons/clock.svg';

import './bookableUnitDetails.scss';

export function BookableUnitDetails({
	unit,
	reservationFormShowing = false,
	onCreateBooking,
	handleToggleUnitReservationDrawer: handleToggleDrawerProp,
	facility,
	storageType,
	reservationFormRef,
	savedFormValues,
	onFormValuesChange,
}) {
	const handleToggleUnitReservationDrawer = useCallback(() => {
		handleToggleDrawerProp(unit, storageType);
	}, [handleToggleDrawerProp, unit, storageType]);
	const isScarce = getScarcityFlag([unit]);
	const { regular, meta } = unit.priceDetails;
	const isOnlineMoveIn = facility.hasOnlineMoveins;

	return (
		<div
			className={classnames('bookable-unit-details', {
				clickable: !reservationFormShowing,
			})}
		>
			<UnitListItem onClick={handleToggleUnitReservationDrawer}>
				<div className="unit-details">
					<div className="left-side">
						<div className="unit-img-container">
							<UnitIcon
								unitSize={`${unit.sizeBucket}-${unit.unitType}`}
								alt="Unit Size"
								lazyLoad={true}
							/>
						</div>
						<UnitDescription
							unitSize={unit.sizeText}
							unitType={unit.unitTypeText}
							amenitiesText={unit.amenitiesText}
							promotion={trim(unit.promotion)}
							tierDescription={unit.tierDescription}
							tierName={unit.tierName}
							giftCardOffer={facility?.giftCardOffer}
						/>
					</div>
					<div className="right-side">
						<div className="right-side-content">
							<div className="price-column">
								<UnitPrice
									price={meta.text}
									regularPrice={
										regular && regular.value !== meta.value
											? regular.text
											: null
									}
									linkedPrice={false}
									showPeriod
									unitQuantity={unit.quantity}
									isScarce={isScarce}
									showFees={facility.showFeeWarning}
									fees={facility.fees}
								/>
							</div>

							<span
								className={classnames(
									'ph-button button-primary',
									{
										'button-gray': reservationFormShowing,
										'button-pink': !reservationFormShowing,
									},
								)}
								id="render-reservation-form-button"
							>
								<span className="button-content">
									{reservationFormShowing
										? 'Close'
										: 'Continue'}
								</span>
							</span>
						</div>
					</div>
				</div>
			</UnitListItem>
			<Expand showing={reservationFormShowing}>
				<div className="reservation-form-container">
					<IconText
						size="small"
						className="lock-in-rate"
						src={clockImage}
					>
						Lock in this rate now. No credit card. No obligation.
					</IconText>
					<UnitReservationForm
						ref={reservationFormRef}
						onSubmit={onCreateBooking}
						unit={unit}
						facility={facility}
						isOMI={isOnlineMoveIn}
						onCreateBooking={onCreateBooking}
						isScarce={isScarce}
						savedFormValues={savedFormValues}
						onFormValuesChange={onFormValuesChange}
					/>
				</div>
			</Expand>
		</div>
	);
}

BookableUnitDetails.propTypes = {
	unit: PropTypes.object.isRequired,
	facility: PropTypes.object.isRequired,
	handleToggleUnitReservationDrawer: PropTypes.func.isRequired,
	reservationFormShowing: PropTypes.bool,
	onCreateBooking: PropTypes.func,
	storageType: PropTypes.string,
	reservationFormRef: PropTypes.object,
};

export default React.memo(BookableUnitDetails);
